import { Button, Divider } from "@heroui/react";
import { GameResult, Machine, Game, RosterMember } from "../../types";
import { CoolHeading } from "../CoolHeading";
import approximateScore from "../../utils/approximate-scores";
import { Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { RatingAndElo } from "../RatingAndElo";
import { useMatchFilters } from "../../hooks/useMatchFilters";
import { useTeamStats } from "../../hooks/useTeamStats";
import { useActiveMatch } from "../../hooks/useActiveMatch";
import { NoDataAvailable } from "../NoDataAvailable";

export function Games() {
  const { selectedPlayers, selectedMachines, pickOrRespond, singlesOrDoubles } =
    useMatchFilters();

  const { data, loading } = useTeamStats();

  const { match } = useActiveMatch();

  function filterResults() {
    return data.matches
      .sort((a, b) => {
        const dateComparison =
          new Date(b.date).getTime() - new Date(a.date).getTime();
        if (dateComparison !== 0) return dateComparison;
        return parseInt(b.round) - parseInt(a.round);
      })
      .filter((m) => {
        return m.playerScores.some(
          (p) => match.teamId === p.teamId && p.activeOnCurrentTeam
        );
      })
      .map((match, i) => {
        const machine = data.machines.find(
          (machine) => machine.machineGroupId === match.machineGroupId
        );

        return {
          machine,
          match,
          index: i,
        } as GameResult;
      })
      .filter(({ machine, match }) => {
        const playerMatches = match.playerScores.some((p) =>
          selectedPlayers.includes(p.playerId)
        );
        const machineMatches = selectedMachines.includes(
          machine.machineGroupId
        );
        const pickOrRespondMatches =
          !pickOrRespond || pickOrRespond.includes(match.pickOrRespond);
        const singlesOrDoublesMatches =
          !singlesOrDoubles ||
          singlesOrDoubles.includes(match.singlesOrDoubles);

        return (
          playerMatches &&
          machineMatches &&
          pickOrRespondMatches &&
          singlesOrDoublesMatches
        );
      });
  }

  const [games, setGames] = useState<GameResult[]>([]);

  useEffect(() => {
    if (loading || !data) return;
    setGames(filterResults());
  }, [
    loading,
    selectedPlayers,
    selectedMachines,
    pickOrRespond,
    singlesOrDoubles,
    data,
  ]);

  const initiallyVisible = 24;
  const [showAll, setShowAll] = useState(false);
  const hasTooManyToShowAtOnce = games.length > initiallyVisible;

  const visibleResults =
    hasTooManyToShowAtOnce && !showAll
      ? games.slice(0, initiallyVisible)
      : games;

  return (
    <div className="flex w-full flex-col">
      {games.length === 0 ? (
        <NoDataAvailable pluralNoun="games" />
      ) : (
        <>
          <div className="grid w-full grid-cols-1 flex-col gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visibleResults.map((result, i) => (
              <div key={i} className="flex flex-col">
                <GameCard
                  key={i}
                  game={result.match}
                  isWin={result.match.win}
                  machine={result.machine}
                  highlightedTeamId={match.opponent__v2.id}
                  roster={match.opponent__v2.roster}
                />
              </div>
            ))}
          </div>
          {hasTooManyToShowAtOnce && !showAll && (
            <div className="mt-4 flex justify-center">
              <Button onPress={() => setShowAll(true)}>
                {`Show ${games.length - initiallyVisible} more`}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GameHeader({ game, machine }: { game: Game; machine: Machine }) {
  return (
    <div className="text-2xs mb-1 flex w-full items-center justify-between gap-1 overflow-hidden px-1">
      <>
        {machine.machineGroupName && (
          <div className="text-2xs flex min-w-0 flex-1 items-center gap-1 pr-1 font-bold text-secondary">
            <CoolHeading
              variant="fixed"
              className="overflow-hidden text-ellipsis text-nowrap py-1 text-[1rem]"
            >
              {machine.machineGroupName}
            </CoolHeading>
          </div>
        )}
        <span className="text-xs">
          {new Date(game.date + "T00:00:00-08:00").toLocaleDateString()}, R
          {game.round}
          {game.round === game.simulatedHomeRoundNumber ? "" : "*"}
        </span>
      </>
    </div>
  );
}

function GameCard({
  game,
  machine,
  roster,
  isWin,
  highlightedTeamId,
}: {
  game: Game;
  machine: Machine;
  roster: RosterMember[];
  isWin?: boolean;
  highlightedTeamId: number;
}) {
  const playersGroupedByTeam = game.playerScores.reduce(
    (acc, ps) => {
      if (!acc[ps.teamId]) {
        acc[ps.teamId] = [];
      }
      // Find player in roster
      const rosterMember = roster.find((r) => r.id === ps.playerId);
      acc[ps.teamId].push({
        ...ps,
        elo: rosterMember?.elo ?? null,
      });
      return acc;
    },
    {} as { [teamId: number]: PlayerScoreProps[] }
  );

  const teams = Object.keys(playersGroupedByTeam).sort((a, b) => {
    const hasOrder1 = (team: string) =>
      playersGroupedByTeam[parseInt(team)].some((ps) => ps.order === 1);

    if (hasOrder1(a)) return -1;
    if (hasOrder1(b)) return 1;
    return 0;
  });

  return (
    <div className="flex h-full flex-col">
      <GameHeader game={game} machine={machine} />
      <div
        className={`flex h-full flex-1 flex-col justify-between rounded-lg p-2 text-black ${isWin ? "bg-[#b8eb9e]" : "bg-red-200"}`}
      >
        <div className="flex h-full flex-1 flex-col gap-2 overflow-hidden">
          {teams.map((teamIdStr, idx) => (
            <Fragment key={teamIdStr}>
              <Team
                teamId={parseInt(teamIdStr)}
                players={playersGroupedByTeam[parseInt(teamIdStr)]}
                highlightedTeamId={highlightedTeamId}
              />
              {idx < teams.length - 1 && <Divider />}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function Team({
  teamId,
  players,
  highlightedTeamId,
}: {
  teamId: number;
  players: PlayerScoreProps[];
  highlightedTeamId: number;
}) {
  return (
    <div
      className={`flex h-full flex-col gap-2 rounded-lg bg-inherit py-2 ${highlightedTeamId === teamId ? "font-bold" : "font-normal"}`}
    >
      {players
        .sort((a, b) => {
          return a.order - b.order;
        })
        .map((ps) => (
          <PlayerScore
            key={ps.playerId}
            ps={ps}
            highlightedTeamId={highlightedTeamId}
          />
        ))}
    </div>
  );
}

interface PlayerScoreProps {
  teamId: number;
  playerId: number;
  name: string;
  playerRating: number;
  elo: number | null;
  score: number;
  order: number;
  activeOnCurrentTeam: boolean;
}

function PlayerScore({
  ps,
  highlightedTeamId,
}: {
  ps: PlayerScoreProps;
  highlightedTeamId: number;
}) {
  const nonactive = highlightedTeamId === ps.teamId && !ps.activeOnCurrentTeam;

  return (
    <div
      key={ps.playerId}
      className={`flex h-full w-full items-center justify-between rounded-md px-2 py-1`}
    >
      <div className="flex items-center gap-2 overflow-hidden">
        <span className="text-xs text-gray-400/50">P{ps.order}</span>
        <RatingAndElo rating={ps.playerRating} elo={null} />
        <span
          className={`mr-2 overflow-hidden text-ellipsis text-left normal-case ${nonactive ? "line-through" : ""}`}
        >
          {ps.name}
        </span>
      </div>
      <span className="font-mono">{approximateScore(ps.score)}</span>
    </div>
  );
}
